import React, { useMemo } from 'react';
import { View, Text, Platform } from 'react-native';
import type { CalendarEvent, CalendarTheme, CalendarStyleConfig } from '../types';
import { WEEKDAYS } from '../constants';
import { styles } from '../styles';
import { DayGrid } from './DayGrid';

interface WeeklyViewProps {
  currentDate: Date;
  getEventsForDate: (date: Date) => CalendarEvent[];
  isDateToday: (date: Date) => boolean;
  onDatePress: (day: number) => void;
  onEventDetailsPress?: (event: CalendarEvent) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onShowAllEvents?: (date: Date, events: CalendarEvent[]) => void;
  renderEventBadge: (event: CalendarEvent, small?: boolean) => React.ReactNode;
  theme: CalendarTheme;
  getWeekDates: (date: Date) => Date[];
  styleConfig?: CalendarStyleConfig;
  isLoading?: boolean;
}

/**
 * WeeklyView Component
 * Displays a single week with platform-specific layouts:
 * - Mobile: Vertical stack of 7 day cards
 * - Web: Horizontal grid with 7 columns
 */
export const WeeklyView: React.FC<WeeklyViewProps> = ({
  currentDate,
  getEventsForDate,
  isDateToday,
  onDatePress,
  onEventDetailsPress,
  onEventPress,
  onShowAllEvents,
  renderEventBadge,
  theme,
  getWeekDates,
  isLoading = false,
}) => {
  const weekDates = getWeekDates(currentDate);
  const isMobile = Platform.OS === 'ios' || Platform.OS === 'android';
  const weeklyContainerThemeStyle = useMemo(
    () => ({
      backgroundColor: theme.surfaceColor || theme.backgroundColor,
      shadowColor: theme.shadowColor,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 1,
      shadowRadius: 16,
      elevation: 4,
      borderRadius: 8,
      boxShadow: theme.shadowColor ? `0px 6px 16px 0px ${theme.shadowColor}` : undefined,
    }),
    [theme.backgroundColor, theme.shadowColor, theme.surfaceColor]
  );

  if (isMobile) {
    // Mobile weekly view - vertical stack layout
    return (
      <View style={[styles.weeklyContainer, weeklyContainerThemeStyle]}>
        <View style={styles.weeklyGrid}>
          {weekDates.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const today = isDateToday(date);

            return (
              <View key={index} style={styles.weeklyDayGroup}>
                <View style={styles.weeklyDayNameContainer}>
                  <Text
                    style={[
                      styles.weeklyDayName,
                      { color: theme.headerTextColor },
                    ]}
                  >
                    {WEEKDAYS[date.getDay()]}
                  </Text>
                </View>
                <DayGrid
                  key={index}
                  date={date}
                  dayEvents={dayEvents}
                  today={today}
                  borderColor={theme.borderColor}
                  dayNumberColor={theme.dayNumberColor}
                  disabledDayColor={theme.disabledDayColor}
                  todayAccentColor={theme.todayAccentColor}
                  disabledBackgroundColor={theme.disabledBackgroundColor}
                  cellBackgroundColor={theme.surfaceColor || theme.backgroundColor}
                  overflowBadgeBackgroundColor={theme.overflowBadgeBackgroundColor}
                  overflowBadgeTextColor={theme.overflowBadgeTextColor}
                  onDatePress={onDatePress}
                  onEventDetailsPress={onEventDetailsPress}
                  onEventPress={onEventPress}
                  onShowAllEvents={() => onShowAllEvents?.(date, dayEvents)}
                  renderEventBadge={renderEventBadge}
                  variant="weekly-mobile"
                  isLoading={isLoading}
                />
              </View>
            );
          })}
        </View>
      </View>
    );
  } else {
    // Web weekly view - horizontal grid layout
    return (
      <View style={[styles.weeklyContainer, weeklyContainerThemeStyle]}>
        <View style={styles.weeklyHeaderRow}>
          {weekDates.map((date, index) => (
            <View key={index} style={styles.weeklyHeaderCell}>
              <Text
                style={[
                  styles.weeklyDayName,
                  { color: theme.headerTextColor },
                ]}
              >
                {WEEKDAYS[date.getDay()]}
              </Text>
            </View>
          ))}
        </View>
        <View style={styles.weeklyGridHorizontal}>
          {weekDates.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const today = isDateToday(date);

            return (
              <DayGrid
                key={index}
                date={date}
                dayEvents={dayEvents}
                today={today}
                borderColor={theme.borderColor}
                dayNumberColor={theme.dayNumberColor}
                disabledDayColor={theme.disabledDayColor}
                todayAccentColor={theme.todayAccentColor}
                disabledBackgroundColor={theme.disabledBackgroundColor}
                cellBackgroundColor={theme.surfaceColor || theme.backgroundColor}
                overflowBadgeBackgroundColor={theme.overflowBadgeBackgroundColor}
                overflowBadgeTextColor={theme.overflowBadgeTextColor}
                onDatePress={onDatePress}
                onEventDetailsPress={onEventDetailsPress}
                onEventPress={onEventPress}
                onShowAllEvents={() => onShowAllEvents?.(date, dayEvents)}
                renderEventBadge={renderEventBadge}
                variant="weekly-web"
                isLoading={isLoading}
              />
            );
          })}
        </View>
      </View>
    );
  }
};

export default WeeklyView;
